import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  FolderKanban, 
  Plus, 
  Link2, 
  Building2, 
  Calendar,
  PoundSterling,
  CheckCircle,
  Clock,
  AlertCircle,
  ExternalLink,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [sites, setSites] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [searchJob, setSearchJob] = useState('');

  const [newProject, setNewProject] = useState({
    name: '',
    site_id: '',
    site_name: '',
    description: '',
    budget: '',
    start_date: '',
    end_date: '',
    project_manager: ''
  });

  const loadProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/phs/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(res.data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSites = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/parks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(res.data.parks || res.data || []);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  }, []);

  const searchJobs = useCallback(async (search) => {
    if (!search || search.length < 2) {
      setJobs([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/fm/jobs?search=${encodeURIComponent(search)}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(res.data.jobs || []);
    } catch (error) {
      console.error('Error searching jobs:', error);
    }
  }, []);

  useEffect(() => {
    loadProjects();
    loadSites();
  }, [loadProjects, loadSites]);

  const handleCreateProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const site = sites.find(s => s.id === newProject.site_id || s._id === newProject.site_id);
      
      await axios.post(`${API}/api/phs/projects`, {
        ...newProject,
        site_name: site?.name || newProject.site_name,
        budget: newProject.budget ? parseFloat(newProject.budget) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Project created successfully');
      setShowCreateDialog(false);
      setNewProject({ name: '', site_id: '', site_name: '', description: '', budget: '', start_date: '', end_date: '', project_manager: '' });
      loadProjects();
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const handleLinkJob = async (jobId) => {
    if (!selectedProject) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/phs/projects/${selectedProject.id}/link-job`, {
        job_id: jobId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Job linked to project');
      setShowLinkDialog(false);
      loadProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link job');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-blue-100 text-blue-700';
      case 'on_hold': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6" data-testid="projects-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FolderKanban className="h-6 w-6 text-teal-600" />
              Projects
            </h1>
            <p className="text-gray-500 text-sm mt-1">Group and track related jobs under projects</p>
          </div>
          
          <Button 
            className="bg-teal-600 hover:bg-teal-700"
            onClick={() => setShowCreateDialog(true)}
            data-testid="create-project-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
          </div>
        ) : projects.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FolderKanban className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Projects Yet</h3>
              <p className="text-gray-500 mb-4">Create your first project to start grouping jobs</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Project
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <Card key={project.id} className="hover:shadow-lg transition-shadow" data-testid={`project-card-${project.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <Building2 className="h-3 w-3" />
                        {project.site_name || 'No site'}
                      </CardDescription>
                    </div>
                    <Badge className={getStatusColor(project.status)}>
                      {project.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {project.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
                  )}
                  
                  {/* Budget Progress */}
                  {project.budget && (
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Budget</span>
                        <span>£{(project.spent || 0).toLocaleString()} / £{project.budget.toLocaleString()}</span>
                      </div>
                      <Progress 
                        value={((project.spent || 0) / project.budget) * 100} 
                        className="h-2"
                      />
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{project.job_count || 0} jobs</span>
                    </div>
                    {project.start_date && (
                      <div className="flex items-center gap-1 text-gray-500">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(project.start_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => {
                        setSelectedProject(project);
                        setShowLinkDialog(true);
                      }}
                    >
                      <Link2 className="h-3 w-3 mr-1" />
                      Link Job
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => window.location.href = `/projects/${project.id}`}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Project Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Project</DialogTitle>
              <DialogDescription>
                Group related jobs under a single project for tracking and reporting.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Project Name *</Label>
                <Input 
                  value={newProject.name}
                  onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                  placeholder="e.g., Roof Replacement - Block A"
                  data-testid="project-name-input"
                />
              </div>
              
              <div>
                <Label>Site / Park</Label>
                <Select 
                  value={newProject.site_id} 
                  onValueChange={(v) => setNewProject({...newProject, site_id: v})}
                >
                  <SelectTrigger data-testid="project-site-select">
                    <SelectValue placeholder="Select site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id || site._id} value={site.id || site._id}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea 
                  value={newProject.description}
                  onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                  placeholder="Project scope and details..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Budget (£)</Label>
                  <Input 
                    type="number"
                    value={newProject.budget}
                    onChange={(e) => setNewProject({...newProject, budget: e.target.value})}
                    placeholder="10000"
                  />
                </div>
                <div>
                  <Label>Project Manager</Label>
                  <Input 
                    value={newProject.project_manager}
                    onChange={(e) => setNewProject({...newProject, project_manager: e.target.value})}
                    placeholder="Name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input 
                    type="date"
                    value={newProject.start_date}
                    onChange={(e) => setNewProject({...newProject, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input 
                    type="date"
                    value={newProject.end_date}
                    onChange={(e) => setNewProject({...newProject, end_date: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleCreateProject}
                disabled={!newProject.name}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="confirm-create-project-btn"
              >
                Create Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Job Dialog */}
        <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Link Job to {selectedProject?.name}</DialogTitle>
              <DialogDescription>
                Search for a job to link to this project
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input 
                  value={searchJob}
                  onChange={(e) => {
                    setSearchJob(e.target.value);
                    searchJobs(e.target.value);
                  }}
                  placeholder="Search by job reference or title..."
                  className="pl-10"
                  data-testid="search-job-input"
                />
              </div>

              {jobs.length > 0 && (
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reference</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobs.map((job) => (
                        <TableRow key={job.id || job._id}>
                          <TableCell className="font-mono text-sm">{job.job_reference || job.id}</TableCell>
                          <TableCell className="max-w-48 truncate">{job.title}</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleLinkJob(job.job_reference || job.id)}
                              data-testid={`link-job-${job.job_reference || job.id}`}
                            >
                              <Link2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
